import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen, ArrowRight, Search, FileText, Lightbulb, Target,
  BarChart3, Users, Beaker, Globe, Layers, CheckCircle2,
  TrendingUp, Compass, Shield, Clock, Sparkles, Star,
  BookMarked, PenTool, Award, GraduationCap, Zap
} from 'lucide-react';
import ResourcesLayout from './ResourcesLayout';
import './ResearchGuidesPage.css';

const ResearchGuidesPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const isVisible = (id) => visibleSections.has(id);

  const categories = [
    { id: 'all', label: 'All Guides' },
    { id: 'getting-started', label: 'Getting Started' },
    { id: 'collaboration', label: 'Collaboration' },
    { id: 'publishing', label: 'Publishing' },
    { id: 'tools', label: 'Tools & Methods' },
  ];

  const stats = [
    { value: '50+', label: 'Research Guides' },
    { value: '200K+', label: 'Researchers Helped' },
    { value: '4.9/5', label: 'Average Rating' },
    { value: 'Weekly', label: 'New Content' },
  ];

  const featuredGuides = [
    {
      slug: 'complete-literature-reviews',
      icon: Compass,
      category: 'getting-started',
      title: 'The Complete Guide to Literature Reviews',
      description: 'Learn systematic search strategies, evaluation frameworks, and synthesis methods using our 200M+ paper database.',
      readTime: '15 min read',
      tag: 'Essential',
      tagColor: 'green',
      difficulty: 'Beginner',
    },
    {
      slug: 'high-performance-research-teams',
      icon: Users,
      category: 'collaboration',
      title: 'Building High-Performance Research Teams',
      description: 'Best practices for assembling teams, setting goals, managing communications, and leveraging Research Labs.',
      readTime: '12 min read',
      tag: 'Popular',
      tagColor: 'blue',
      difficulty: 'Intermediate',
    },
    {
      slug: 'writing-papers-published',
      icon: PenTool,
      category: 'publishing',
      title: 'Writing Papers That Get Published',
      description: 'From structuring manuscripts to navigating peer review. Includes templates and expert tips.',
      readTime: '20 min read',
      tag: 'Featured',
      tagColor: 'purple',
      difficulty: 'Advanced',
    },
  ];

  const allGuides = [
    {
      slug: 'mastering-academic-search',
      icon: Search,
      category: 'getting-started',
      title: 'Mastering Academic Search',
      description: 'Advanced search operators, semantic search, citation tracking, and building reading lists.',
      readTime: '8 min read',
      difficulty: 'Beginner',
    },
    {
      slug: 'first-research-lab',
      icon: Beaker,
      category: 'collaboration',
      title: 'Setting Up Your First Research Lab',
      description: 'Step-by-step walkthrough of creating a lab, inviting collaborators, and configuring tools.',
      readTime: '10 min read',
      difficulty: 'Beginner',
    },
    {
      slug: 'research-methodology-framework',
      icon: Target,
      category: 'tools',
      title: 'Research Methodology Framework',
      description: 'Choosing the right methodology — qualitative, quantitative, and mixed-methods compared.',
      readTime: '18 min read',
      difficulty: 'Intermediate',
    },
    {
      slug: 'data-analysis-best-practices',
      icon: BarChart3,
      category: 'tools',
      title: 'Data Analysis Best Practices',
      description: 'Statistical methods, visualization techniques, reproducibility checklists, and tools.',
      readTime: '14 min read',
      difficulty: 'Advanced',
    },
    {
      slug: 'research-ethics-integrity',
      icon: Shield,
      category: 'publishing',
      title: 'Research Ethics & Integrity',
      description: 'Authorship guidelines, data management, IRB protocols, and open science principles.',
      readTime: '11 min read',
      difficulty: 'Beginner',
    },
    {
      slug: 'cross-institutional-collaboration',
      icon: Globe,
      category: 'collaboration',
      title: 'Cross-Institutional Collaboration',
      description: 'Managing multi-institution projects, IP considerations, and data sharing agreements.',
      readTime: '13 min read',
      difficulty: 'Advanced',
    },
    {
      slug: 'maximizing-research-impact',
      icon: TrendingUp,
      category: 'publishing',
      title: 'Maximizing Research Impact',
      description: 'Beyond h-index: professional profiles, social media, preprints, and open access.',
      readTime: '9 min read',
      difficulty: 'Intermediate',
    },
    {
      slug: 'ai-tools-researchers',
      icon: Sparkles,
      category: 'tools',
      title: 'AI Tools for Researchers',
      description: 'Responsible use of AI in research — from literature analysis to drafting with disclosure guidelines.',
      readTime: '12 min read',
      difficulty: 'Intermediate',
    },
    {
      slug: 'phd-survival-guide',
      icon: GraduationCap,
      category: 'getting-started',
      title: 'PhD Survival Guide',
      description: 'Navigating your doctoral journey — advisor relationships, time management, and well-being.',
      readTime: '16 min read',
      difficulty: 'Beginner',
    },
  ];

  const filteredGuides = activeCategory === 'all'
    ? allGuides
    : allGuides.filter((g) => g.category === activeCategory);

  return (
    <ResourcesLayout>
      {/* ── Hero ── */}
      <section className="rg-hero">
        <div className="rg-hero-inner">
          <h1 className="rg-hero-title">
            Best practices for <span className="rg-hero-gradient">modern researchers</span>
          </h1>
          <p className="rg-hero-subtitle">
            Expert-curated guides covering every stage of the research lifecycle — 
            from literature review to publication and impact.
          </p>
          <div className="rg-stats-bar">
            {stats.map((stat, i) => (
              <div key={i} className="rg-stat">
                <span className="rg-stat-value">{stat.value}</span>
                <span className="rg-stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured Guides ── */}
      <section
        id="rg-featured"
        className={`rg-section ${isVisible('rg-featured') ? 'rg-visible' : ''}`}
        ref={(el) => (sectionRefs.current['rg-featured'] = el)}
      >
        <div className="rg-section-inner">
          <div className="rg-section-header">
            <span className="rg-section-number">01</span>
            <div>
              <h2 className="rg-section-title">Featured Guides</h2>
              <p className="rg-section-subtitle">Our most popular and impactful guides</p>
            </div>
          </div>

          <div className="rg-featured-grid">
            {featuredGuides.map((guide, i) => (
              <Link key={guide.slug} to={`/guides/${guide.slug}`} className="rg-featured-card" style={{ '--card-delay': `${i * 80}ms` }}>
                <div className="rg-featured-card-top">
                  <div className={`rg-featured-icon rg-featured-icon--${guide.tagColor}`}>
                    <guide.icon size={20} />
                  </div>
                  <span className={`rg-tag rg-tag--${guide.tagColor}`}>{guide.tag}</span>
                </div>
                <h3 className="rg-featured-title">{guide.title}</h3>
                <p className="rg-featured-desc">{guide.description}</p>
                <div className="rg-featured-meta">
                  <span className="rg-meta-time">
                    <Clock size={12} /> {guide.readTime}
                  </span>
                  <span className={`rg-difficulty rg-difficulty--${guide.difficulty.toLowerCase()}`}>
                    {guide.difficulty}
                  </span>
                </div>
                <span className="rg-card-link">
                  Read Guide <ArrowRight size={13} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── All Guides ── */}
      <section
        id="rg-all"
        className={`rg-section rg-section--alt ${isVisible('rg-all') ? 'rg-visible' : ''}`}
        ref={(el) => (sectionRefs.current['rg-all'] = el)}
      >
        <div className="rg-section-inner">
          <div className="rg-section-header">
            <span className="rg-section-number">02</span>
            <div>
              <h2 className="rg-section-title">Browse All Guides</h2>
              <p className="rg-section-subtitle">Filter by category to find what you need</p>
            </div>
          </div>

          <div className="rg-filters">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`rg-filter-btn ${activeCategory === cat.id ? 'rg-filter-btn--active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="rg-guides-grid">
            {filteredGuides.map((guide, i) => (
              <Link key={guide.slug} to={`/guides/${guide.slug}`} className="rg-guide-card" style={{ '--card-delay': `${i * 50}ms` }}>
                <div className="rg-guide-card-top">
                  <div className="rg-guide-icon">
                    <guide.icon size={18} />
                  </div>
                  <span className={`rg-difficulty rg-difficulty--${guide.difficulty.toLowerCase()}`}>
                    {guide.difficulty}
                  </span>
                </div>
                <h3 className="rg-guide-title">{guide.title}</h3>
                <p className="rg-guide-desc">{guide.description}</p>
                <div className="rg-guide-footer">
                  <span className="rg-meta-time">
                    <Clock size={12} /> {guide.readTime}
                  </span>
                  <span className="rg-guide-link">
                    Read <ArrowRight size={12} />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {filteredGuides.length === 0 && (
            <div className="rg-empty">
              <FileText size={36} />
              <p>No guides found in this category yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="rg-cta">
        <div className="rg-cta-inner">
          <span className="rg-cta-badge">
            <Zap size={14} />
            Contribute
          </span>
          <h2 className="rg-cta-title">Want to contribute a guide?</h2>
          <p className="rg-cta-subtitle">
            Share your expertise with the research community. We welcome 
            experienced researchers to contribute guides.
          </p>
          <div className="rg-cta-actions">
            <Link to="/signup" className="rg-btn rg-btn--primary">
              Get Started
              <ArrowRight size={16} />
            </Link>
            <Link to="/resources" className="rg-btn rg-btn--secondary">
              All Resources
            </Link>
          </div>
        </div>
      </section>
    </ResourcesLayout>
  );
};

export default ResearchGuidesPage;
